import React, { FunctionComponent } from 'react';
import dompurify from 'dompurify';
import { CardCTASection } from '../../../Layout/Promotions/PromotionCard/CardCTASection';
import { Heading } from '../../../shared/Heading';
import { Paragraph } from '../../../shared/Paragraph';
import { ProgressBar } from '../../../shared/ProgressBar';
import { PromotionCardProps } from '../types';
import { showCurrency } from '../../../Layout/Promotions/utils';
import { useAccurateWindowWidth } from '../../../../hooks/useDeviceInfo';
import LazyLoadedImage from '../../../Media/LazyLoadedImage';
import { joinStrings } from '../../../../utils/string';
import { PromoTypes } from '../../../Layout/Promotions/types';
import { useReferralUrl } from '../../../../hooks/useReferralUrl';
import { ReferralCopyInput } from '../../ReferralCopyInput';
import './ItvPromoCardRect.scss';

export const ItvPromoCardRect: FunctionComponent<PromotionCardProps> = ({
  promo,
  handleClaimClick,
  goToPromoPage,
  layout,
}): JSX.Element => {
  const sanitizer = dompurify.sanitize;
  const { isTablet, isMobile } = useAccurateWindowWidth();
  const referralUrl = useReferralUrl();

  const { title, description, imgAlt, longImg, rectImg } = promo;

  const isPrizeRefer = promo.prizeRefer ?? promo.promoType?.toLowerCase() === PromoTypes.PRIZE;
  const showReferralInput = isPrizeRefer && Boolean(referralUrl);

  const isHero1 = layout === 'hero-1';
  const isHero2 = layout === 'hero-2';

  const isSimplePanel = !isMobile && (isHero1 || isHero2);

  const desktopImg = (isHero1 && longImg) || rectImg;
  const resultImg = (isMobile && rectImg) || (isTablet && longImg) || desktopImg;

  const onClaimClick = (): void => handleClaimClick?.(promo);
  const stopClickPropagation = (event: React.MouseEvent<HTMLDivElement>): void => event.stopPropagation();

  return (
    <>
      <div className="itv-promo-card-rect" onClick={goToPromoPage}>
        <LazyLoadedImage
          src={resultImg}
          alt={imgAlt}
          className={joinStrings([
            'itv-promo-card-rect__img',
            isTablet || (isHero1 && !isMobile) ? 'itv-promo-card-rect__img--long' : 'itv-promo-card-rect__img--rect',
          ])}
        />
        <div
          className={joinStrings([
            'itv-promo-card-rect__content',
            isSimplePanel && 'itv-promo-card-rect__content--simple',
            isHero1 && 'itv-promo-card-rect__content--long',
          ])}
        >
          <div
            className={joinStrings([
              'itv-promo-card-rect__panel',
              isSimplePanel && 'itv-promo-card-rect__panel--simple',
              isSimplePanel && showReferralInput && 'itv-promo-card-rect__panel--referral-simple',
            ])}
          >
            <div className="itv-promo-card-rect__panel-main">
              <div className="itv-promo-card-rect__titles">
                <Heading className="itv-promo-card-rect__title" noMargin level={4}>
                  {title}
                </Heading>
              </div>
              {promo.isActive && promo.promoType?.toLowerCase() !== PromoTypes.PRIZE && (
                <ProgressBar
                  showWageringInfo
                  value={promo.progress}
                  type="success"
                  min={0}
                  max={promo.target}
                  showCurrency={promo.isCurrencyBased ?? showCurrency(promo?.type)}
                />
              )}
            </div>
            {showReferralInput ? (
              <ReferralCopyInput
                referralUrl={referralUrl}
                rowClassName="itv-promo-card-rect__referral-row"
                inputClassName="itv-promo-card-rect__referral-input"
                inputBoxClassName="itv-promo-card-rect__referral-input-field"
                copyIconClassName="itv-promo-card-rect__copy-icon"
                copiedTextClassName="itv-promo-card-rect__referral-copied"
                rightContent={<CardCTASection onViewDetailsClick={goToPromoPage} promo={promo} showPrimary={false} />}
                onWrapperClick={stopClickPropagation}
              />
            ) : (
              <CardCTASection
                onClaimClick={onClaimClick}
                onViewDetailsClick={goToPromoPage}
                promo={promo}
                showPrimary
              />
            )}
          </div>
          {description && (
            <Paragraph className="itv-promo-card-rect__description" noMargin>
              <span dangerouslySetInnerHTML={{ __html: sanitizer(description) }} />
            </Paragraph>
          )}
        </div>
      </div>
    </>
  );
};
